import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { X, ChevronRight, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import SeatMap, { SeatLegend } from "@/components/SeatMap";
import CheckoutDialog from "@/components/CheckoutDialog";
import { Amenities } from "@/components/TripCard";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useNavigate, useLocation } from "react-router-dom";
import { money, fmtTime, fmtDate, addMinutes } from "@/lib/format";

const MAX = 10;

// Shows the bus's full stopping pattern and highlights the passenger's part of it
export function RouteLine({ trip }) {
  if (!trip.stops || trip.stops.length < 3) return null;
  return (
    <div className="mt-3 flex flex-wrap items-center gap-1 text-[11px] font-semibold" data-testid="trip-route-stops">
      {trip.stops.map((s, i) => {
        const mine = i >= trip.from_idx && i <= trip.to_idx;
        return (
          <span key={s.station_id} className="flex items-center gap-1">
            {i > 0 && <span className={`h-0.5 w-3 ${i > trip.from_idx && i <= trip.to_idx ? "bg-[#0052FF]" : "bg-slate-200"}`} />}
            <span className={`chip ${mine ? "bg-blue-600 text-white" : "bg-slate-100 text-slate-500"}`}>{s.city} {fmtTime(s.departure)}</span>
          </span>
        );
      })}
    </div>
  );
}

export default function BookingFlow({ tripId, from, to, mode = "public", onBooked }) {
  const [trip, setTrip] = useState(null);
  const [selected, setSelected] = useState([]);
  const [open, setOpen] = useState(false);
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const proceed = () => {
    if (mode === "public" && !user) { toast.error("Please log in to book your ticket"); navigate("/login", { state: { next: location.pathname + location.search } }); return; }
    setOpen(true);
  };

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/public/trips/${tripId}`, { params: { from_id: from || undefined, to_id: to || undefined } });
      setTrip(data);
      setSelected((s) => s.filter((n) => !data.taken_seats.includes(n)));
    } catch (e) { toast.error(errMsg(e)); }
  }, [tripId, from, to]);

  useEffect(() => { setSelected([]); load(); }, [load]);

  const toggle = (n) => {
    if (selected.includes(n)) return setSelected(selected.filter((x) => x !== n));
    if (selected.length >= MAX) return toast.error(`You can book up to ${MAX} seats at a time`);
    setSelected([...selected, n].sort((a, b) => a - b));
  };

  if (!trip) return <div className="grid h-64 place-items-center"><Loader2 className="h-6 w-6 animate-spin text-[#0052FF]" /></div>;

  return (
    <div className="grid gap-6 lg:grid-cols-[auto_1fr]" data-testid="booking-flow">
      <div className="flex flex-col items-center gap-3">
        <SeatMap capacity={trip.capacity} layout={trip.layout} plan={trip.plan} taken={trip.taken_seats} selected={selected} onToggle={toggle} />
        <SeatLegend />
      </div>
      <div className="space-y-4 lg:sticky lg:top-24 lg:self-start">
        <div className="card-soft p-5">
          <div className="label-xs">{fmtDate(trip.departure)} · {trip.bus_type}</div>
          <div className="mt-1 font-display text-xl font-extrabold">{trip.origin_city} → {trip.destination_city}</div>
          <div className="text-sm text-slate-500">{fmtTime(trip.departure)} from {trip.origin} · arrives ~{addMinutes(trip.departure, trip.duration_min)}</div>
          <RouteLine trip={trip} />
          <div className="mt-3"><Amenities list={trip.amenities} /></div>
          <div className="mt-3 text-xs text-slate-500">Bus {trip.bus_number} · {trip.seats_available} seats left · {money(trip.fare)} per seat</div>
        </div>
        <div className="card-soft p-5" data-testid="seat-summary">
          <div className="label-xs mb-2">Selected seats</div>
          {selected.length === 0 ? (
            <p className="text-sm text-slate-500">Tap green seats on the bus map to select them.</p>
          ) : (
            <div className="flex flex-wrap gap-2">
              {selected.map((n) => (
                <button key={n} onClick={() => toggle(n)} className="chip btn-press bg-blue-600 px-3 py-1.5 text-sm text-white" data-testid={`selected-seat-chip-${n}`}>Seat {n}<X className="h-3.5 w-3.5" /></button>
              ))}
            </div>
          )}
          <div className="mt-4 flex items-end justify-between border-t border-slate-100 pt-4">
            <div><div className="label-xs">Total</div><div className="font-display text-3xl font-extrabold text-slate-900" data-testid="booking-total">{money(trip.fare * selected.length)}</div></div>
            <Button disabled={!selected.length} onClick={proceed} className="h-12 rounded-xl bg-[#0052FF] px-6 text-base font-bold hover:bg-[#0041CC] btn-press" data-testid="continue-to-details-button">
              Continue <ChevronRight className="h-5 w-5" />
            </Button>
          </div>
        </div>
      </div>
      <CheckoutDialog open={open} onOpenChange={setOpen} trip={trip} seats={selected} mode={mode} onConflict={load}
        onBooked={(b) => { setOpen(false); setSelected([]); load(); onBooked(b); }} />
    </div>
  );
}
